import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/hrService';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

// Idle timeout: 10 minutes (600000ms)
const IDLE_TIMEOUT = 10 * 60 * 1000;
// Warning before logout: 1 minute before
const WARNING_BEFORE = 1 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setShowIdleWarning(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (!user) return;

    setShowIdleWarning(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setShowIdleWarning(true);
      toast('You will be logged out in 1 minute due to inactivity', {
        icon: '⏰',
        duration: 10000,
      });
    }, IDLE_TIMEOUT - WARNING_BEFORE);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity');
      logout();
    }, IDLE_TIMEOUT);
  }, [user, logout]);

  // Setup activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    let lastReset = Date.now();

    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 1000) {
        lastReset = now;
        resetIdleTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start timer
    resetIdleTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, resetIdleTimer]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await authService.getCurrentUser();
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          userId: response.data.user_id,
          tempToken: response.data.temp_token
        };
      }

      const { access, refresh } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      const userResponse = await authService.getCurrentUser();
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const verify2FA = async (userId, code, tempToken) => {
    try {
      const response = await authService.verify2FA(userId, code, tempToken);
      const { access, refresh } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      const userResponse = await authService.getCurrentUser();
      setUser(userResponse.data);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid verification code'
      };
    }
  };

  // Refresh user data (useful after enabling 2FA)
  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to refresh user data' };
    }
  };

  const value = {
    user,
    loading,
    login,
    verify2FA,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    requires2FASetup: user && !user.has_2fa_enabled,
    showIdleWarning,
    resetIdleTimer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
